import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FaEdit, FaSave, FaTimes, FaTrash } from "react-icons/fa";
import "../../css/others/Table.css";
import "../../css/others/Popup.css";
import Pagination from "../../Reusablecomponents/Pagination";
import { apiCall } from "../../main/ApiCall";
import ToastPopup from "../../popups/ToastPopup";
import FunctionChecklistDetailsPopup from "../../popups/FunctionChecklistDetailspopup";
import { createToastMessage, ToastMessage } from "../../interFaces/interfaces";
const FunctionCheckListDetails: React.FC = () => {
  const { functionTemplateId } = useParams();
  const [details, setDetails] = useState<any>(null);
  const [, setLoading] = useState<boolean>(true);
  const [newQuestions, setNewQuestions] = useState<any[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editableQuestion, setEditableQuestion] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [showRedirectPopup, setShowRedirectPopup] = useState<boolean>(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; id: string | null; functionQuestionId?: string | null }>({ isOpen: false, id: null, functionQuestionId: null });
  const [savingQuestionId, setSavingQuestionId] = useState<number | null>(null);
  const location = useLocation();
  const { status } = location.state || {};
  const navigate = useNavigate();
  const restrictedChars = /[\\:|/]/;
  useEffect(() => {
    if (status === "ACTIVE") {
      const timer = setTimeout(() => {
        setShowRedirectPopup(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [status]);
  useEffect(() => {
    if (showRedirectPopup) {
      const timer = setTimeout(() => {
        navigate(-1);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showRedirectPopup, navigate]);
  const fetchChecklistDetails = useCallback(async () => {
    if (!functionTemplateId) return;
    setLoading(true);
    try {
      const checklist = await apiCall("get", `/api/function-template/${functionTemplateId}/questions/edit`, null, { page: currentPage - 1, size: itemsPerPage });
      if (checklist && checklist.content) {
        const validatedContent = checklist.content.map((item: any) => ({ ...item, questionId: item.functionQuestionId }));
        setDetails({ ...checklist, content: validatedContent });
        setTotalElements(checklist.totalElements || 0);
        setTotalPages(checklist.totalPages || 0);
      } else {
        setDetails({ content: [] });
      }
    } catch (error) {
      console.error("Error fetching checklist details:", error);
    }
  }, [functionTemplateId, currentPage, itemsPerPage]);
  useEffect(() => {
    fetchChecklistDetails();
  }, [fetchChecklistDetails]);
  const handleAddRow = () => {
    if (status === "ACTIVE") return;
    const newRow = { questionId: Date.now(), functionAreaName: "", questionText: "", evidenceRequired: "", isoClauseReference: "", annexAControlReference: "", isNew: true, isSaving: false };
    setNewQuestions((prev) => [...prev, newRow]);
  };
  const handleRemoveRow = () => {
    if (!deleteConfirm.functionQuestionId || !functionTemplateId || status === "ACTIVE") return;
    apiCall("delete", `/api/function-template/${functionTemplateId}/questions/${deleteConfirm.functionQuestionId}`)
      .then(() => {
        fetchChecklistDetails();
        setMessage(createToastMessage("Question deleted successfully!", "success", 3000));
      })
      .catch((error: any) => {
        const errorMessage = error?.response?.data?.message;
        setMessage(createToastMessage(errorMessage, "error", 3000));
      })
      .finally(() => {
        setDeleteConfirm({ isOpen: false, id: null, functionQuestionId: null });
      });
  };
  const handleSaveSingleQuestion = async (index: number) => {
    if (status === "ACTIVE") return;
    const questionToSave = newQuestions[index];
    setSavingQuestionId(questionToSave.questionId);
    const questionData = { functionAreaName: questionToSave.functionAreaName, questionText: questionToSave.questionText, evidenceRequired: questionToSave.evidenceRequired, isoClauseReference: questionToSave.isoClauseReference || "", annexAControlReference: questionToSave.annexAControlReference || "" };
    try {
      await apiCall("post", `/api/function-template/${functionTemplateId}/questions`, [questionData]);
      setNewQuestions((prev) => prev.filter((_, i) => i !== index));
      fetchChecklistDetails();
      setMessage(createToastMessage("Question added successfully!", "success", 3000));
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      setMessage(createToastMessage(errorMessage, "error", 3000));
    } finally {
      setSavingQuestionId(null);
    }
  };
  const handleEditClick = (id: string) => {
    if (status === "ACTIVE") return;
    const questionToEdit = details?.content.find((item: any) => item.questionId?.toString() === id);
    if (questionToEdit) {
      setEditableQuestion(questionToEdit);
      setIsEditModalOpen(true);
    } else {
      console.error("Question not found with ID:", id);
    }
  };
  const handleModalChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (containsSpecialChars(value)) {
      setMessage(createToastMessage(`Special characters are not allowed - ${restrictedChars}`, "warning", 3000));
      return;
    }
    setEditableQuestion((prev: any) => ({ ...prev, [name]: value }));
  };
  const handleSaveChanges = async () => {
    if (!editableQuestion || status === "ACTIVE") return;
    if (!editableQuestion.questionText.trim() || !editableQuestion.evidenceRequired.trim() || !editableQuestion.functionAreaName.trim()) {
      setMessage(createToastMessage("Please fill all required fields", "warning", 3000));
      return;
    }
    try {
      await apiCall("patch", `/api/function-template/${functionTemplateId}/questions/${editableQuestion.functionQuestionId}`, { functionAreaName: editableQuestion.functionAreaName, questionText: editableQuestion.questionText, evidenceRequired: editableQuestion.evidenceRequired, isoClauseReference: editableQuestion.isoClauseReference, annexAControlReference: editableQuestion.annexAControlReference });
      setMessage(createToastMessage("Question updated successfully!", "success", 3000));
      setIsEditModalOpen(false);
      fetchChecklistDetails();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      setMessage(createToastMessage(errorMessage, "error", 3000));
    }
  };
  const handleCancelNewQuestion = (index: number) => {
    setNewQuestions((prev) => prev.filter((_, i) => i !== index));
  };
  const renderNewQuestionRow = (question: any, index: number) => {
    const isSaving = savingQuestionId === question.questionId;
    return (
      <tr key={`new-${question.questionId}`}>
        <td>{details?.content?.length + index + 1}</td>
        <td>
          <textarea value={question.functionAreaName} onChange={(e) => handleRestrictedInput(e.target.value, () => { const updated = [...newQuestions]; updated[index].functionAreaName = e.target.value; setNewQuestions(updated) })} placeholder="Function area" required disabled={isSaving} />
        </td>
        <td>
          <textarea value={question.questionText} onChange={(e) => handleRestrictedInput(e.target.value, () => { const updated = [...newQuestions]; updated[index].questionText = e.target.value; setNewQuestions(updated); })} placeholder="Enter question" required disabled={isSaving} />
        </td>
        <td>
          <textarea value={question.evidenceRequired} onChange={(e) => handleRestrictedInput(e.target.value, () => { const updated = [...newQuestions]; updated[index].evidenceRequired = e.target.value; setNewQuestions(updated) })} placeholder="Evidence required" required disabled={isSaving} />
        </td>
        <td>
          <textarea value={question.isoClauseReference} onChange={(e) => handleRestrictedInput(e.target.value, () => { const updated = [...newQuestions]; updated[index].isoClauseReference = e.target.value; setNewQuestions(updated) })} placeholder="ISO clause reference" disabled={isSaving} />
        </td>
        <td>
          <textarea value={question.annexAControlReference} onChange={(e) => handleRestrictedInput(e.target.value, () => { const updated = [...newQuestions]; updated[index].annexAControlReference = e.target.value; setNewQuestions(updated); })} placeholder="Annex A control reference" disabled={isSaving} />
        </td>
        <td>
          <FaSave className={`action-button ${isSaving ? 'disabled' : ''}`} onClick={() => handleSaveSingleQuestion(index)} title="Save" />
          <FaTimes className={`action-button ${isSaving ? 'disabled' : ''}`} onClick={() => handleCancelNewQuestion(index)} title="Cancel" />
        </td>
      </tr>
    );
  };
  const containsSpecialChars = (str: string) => {
    return restrictedChars.test(str);
  };
  const handleRestrictedInput = (value: string, updateFn: () => void) => {
    if (containsSpecialChars(value)) {
      setMessage(createToastMessage(`Special characters are not allowed - ${restrictedChars}`, "warning", 3000));
      return;
    }
    updateFn();
  };
  return (
    <div className="table-details">
      <div className="table-title">
        <h2>Function Checklist Details</h2>
        {status !== "ACTIVE" && (
          <button className="add-question-button" onClick={handleAddRow}>Add Question</button>
        )}
      </div>
      <>
        <div className="table-container">
          <div className="table-inner-container">
            <table className="main-table">
              <thead>
                <tr>
                  <th>S.No.</th><th>Function Area</th><th>Question</th><th>Evidence Required</th><th>ISO Clause Ref</th><th>Annex A Control Ref</th>{status !== "ACTIVE" && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                {details?.content?.map((item: any, index: number) => (
                  <tr key={item.questionId}>
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td>{item.functionAreaName || "N/A"}</td>
                    <td>{item.questionText || "N/A"}</td>
                    <td>{item.evidenceRequired || "N/A"}</td>
                    <td>{item.isoClauseReference || "N/A"}</td>
                    <td>{item.annexAControlReference || "N/A"}</td>
                    {status !== "ACTIVE" && (
                      <td>
                        <FaEdit className="action-button" onClick={() => handleEditClick(item.questionId.toString())} title="Edit" />
                        <FaTrash className="action-button" onClick={() => setDeleteConfirm({ isOpen: true, id: item.questionId.toString(), functionQuestionId: item.functionQuestionId?.toString() })} title="Delete" />
                      </td>
                    )}
                  </tr>
                ))}
                {status !== "ACTIVE" && newQuestions.map((question, index) =>
                  renderNewQuestionRow(question, index)
                )}
                {details?.content?.length === 0 && newQuestions.length === 0 && (
                  <tr>
                    <td colSpan={7} className="no-data"> No questions available for this function checklist</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <Pagination currentPage={currentPage} totalPages={totalPages} totalElements={totalElements} itemsPerPage={itemsPerPage} onPageChange={setCurrentPage} onItemsPerPageChange={setItemsPerPage} />
      </>
      {isEditModalOpen && editableQuestion && (
        <FunctionChecklistDetailsPopup question={editableQuestion} onClose={() => setIsEditModalOpen(false)} onSave={handleSaveChanges} onChange={handleModalChange} />
      )}
      {deleteConfirm.isOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this question?</p>
            <div className="buttons">
              <button className="cancelbutton" onClick={() => setDeleteConfirm({ isOpen: false, id: null, functionQuestionId: null })} > Cancel </button>
              <button className="savebutton" onClick={handleRemoveRow}>Delete</button>
            </div>
          </div>
        </div>
      )}
      {showRedirectPopup && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Redirection Notice</h3>
            <p>This Function checklist is in ACTIVE status. Redirecting to draft version...</p>
          </div>
        </div>
      )}
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}    </div>
  );
};
export default FunctionCheckListDetails;